import { Badge } from '@svs/ui/react/badge';
import type { ReactNode } from 'react';

import { cn } from '../../../utils/entry.js';
import { DRAW_STATE_TEXTS } from '../../constants.js';
import type { DrawStatus } from '../../utils/getDrawStatus.js';
import shouldShowStatus from '../../utils/shouldShowStatus.js';
import DrawCardPanel from './DrawCard.Panel.js';

function getDrawCardStatus(status: string | undefined, isSelector?: boolean): ReactNode {
  if (status === 'Cancelled') {
    return (
      <div className={cn('text-important font-bold', isSelector ? 'text-[15px]' : 'text-xl')}>
        {DRAW_STATE_TEXTS.CANCELLED}
      </div>
    );
  }

  if (status === 'Live') {
    return (
      <Badge
        emphasis="low"
        variant="secondary"
        size="xl"
        className={cn('inline-block w-fit font-bold', isSelector ? 'text-[15px]' : 'text-xl')}
      >
        Live
      </Badge>
    );
  }

  if (status === 'Finished') {
    return <div className={cn('font-bold', isSelector ? 'text-[15px]' : 'text-xl')}>{DRAW_STATE_TEXTS.FINISHED}</div>;
  }

  if (status === 'Defined') {
    return <div className={cn('font-bold', isSelector ? 'text-[15px]' : 'text-xl')}>{DRAW_STATE_TEXTS.NOT_OPENED}</div>;
  }

  if (status === 'TemporaryClosed') {
    return (
      <div
        className={cn('flex flex-row items-center justify-center font-medium', isSelector ? 'text-[15px]' : 'text-xl')}
      >
        <span className="grow text-xl font-bold">Omgången är tillfälligt stängd. Välkommen åter.</span>
        <a href="/installda-matcher" className="text-body-100 ml-10 align-baseline text-sm underline">
          Mer information här.
        </a>
      </div>
    );
  }

  return undefined;
}

type DrawCardStatusDesktopProps = {
  status: DrawStatus;
  isSelector?: boolean;
  isSelected?: boolean;
};

/**
 * Component for draw card status shown in draw card.
 *
 * @param root0 Component props
 * @param root0.status the status of the draw to determine which status message to show
 * @param root0.isSelector Whether the draw card is used as a selector
 * @param root0.isSelected Whether the draw card is selected in the selector
 * @returns component that renders the status for a draw
 */
export default function DrawCardStatusDesktop({
  status,
  isSelector,
  isSelected,
}: DrawCardStatusDesktopProps): ReactNode {
  const drawCardStatus = getDrawCardStatus(status, isSelector);

  return (
    <DrawCardPanel
      ariaLabel={`status: ${status}`}
      className={cn(shouldShowStatus(status) ? 'hidden lg:flex' : 'hidden')}
    >
      <div
        className={cn(
          'text-surface-fg-muted',
          isSelector ? 'text-sm' : 'text-base',
          isSelector && isSelected && 'font-bold'
        )}
      >
        Status
      </div>
      {drawCardStatus}
    </DrawCardPanel>
  );
}
